/**
 * OrgTreeView — Interactive organization hierarchy with drag-and-drop.
 *
 * Features:
 *  - Visual tree with connecting lines
 *  - Drag-and-drop to reassign reporting (grip handle on each node)
 *  - Click a node to select → show inline actions
 *  - Collapsible branches
 *  - Unassigned users pool (draggable into tree)
 *  - Drop-to-unassign zone (appears during drag)
 *  - Role color coding
 */
import React, { useState, useMemo, useCallback, useRef } from 'react';
import Users from 'lucide-react/dist/esm/icons/users';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import GitBranch from 'lucide-react/dist/esm/icons/git-branch';
import UserPlus from 'lucide-react/dist/esm/icons/user-plus';
import ArrowUpRight from 'lucide-react/dist/esm/icons/arrow-up-right';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import GripVertical from 'lucide-react/dist/esm/icons/grip-vertical';
import type { OrgTreeNode, OrgUser } from '../../../services/api/org';

const ROLE_BORDER: Record<string, string> = {
  platform_admin: 'border-red-400/30',
  tenant_admin: 'border-purple-400/30',
  org_admin: 'border-blue-400/30',
  user: 'border-subtle',
};

const ROLE_BG: Record<string, string> = {
  platform_admin: 'from-red-400/10 to-red-400/5',
  tenant_admin: 'from-purple-400/10 to-purple-400/5',
  org_admin: 'from-blue-400/10 to-blue-400/5',
  user: 'from-white/[0.02] to-transparent',
};

const ROLE_DOT: Record<string, string> = {
  platform_admin: 'bg-status-error',
  tenant_admin: 'bg-purple-400',
  org_admin: 'bg-blue-400',
  user: 'bg-surface-elevated',
};

const ROLE_LABELS: Record<string, string> = {
  platform_admin: 'Platform Admin',
  tenant_admin: 'Tenant Admin',
  org_admin: 'Org Admin',
  user: 'User',
};

// ── Drag-and-drop helpers ──

/** Find a node by ID anywhere in the tree */
function findNode(nodes: OrgTreeNode[], id: string): OrgTreeNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const found = findNode(n.reports, id);
    if (found) return found;
  }
  return null;
}

/** Check if targetId lives anywhere in node's subtree (cycle prevention) */
function isInSubtree(node: OrgTreeNode, targetId: string): boolean {
  for (const child of node.reports) {
    if (child.id === targetId) return true;
    if (isInSubtree(child, targetId)) return true;
  }
  return false;
}

// ── Types ──

interface OrgTreeViewProps {
  tree: OrgTreeNode[];
  loading: boolean;
  users?: OrgUser[];
  isAdmin?: boolean;
  onSelectUser?: (userId: string) => void;
  onReassignManager?: (userId: string, newManagerId: string | null) => void;
  onInviteUnder?: (managerId: string) => void;
  onAssignExisting?: (managerId: string) => void;
}

/** Drag context shared across all tree nodes */
interface DragCtx {
  canDrag: boolean;
  draggedId: string | null;
  dropTargetId: string | null;
  isValidDrop: (targetId: string) => boolean;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragEnd: () => void;
  onNodeDragOver: (e: React.DragEvent, id: string) => void;
  onNodeDrop: (e: React.DragEvent, targetId: string) => void;
}

// ── Tree Node Component ──

const TreeNode: React.FC<{
  node: OrgTreeNode;
  depth: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  collapsed: Set<string>;
  onToggleCollapse: (id: string) => void;
  onSelectUser?: (userId: string) => void;
  onInviteUnder?: (managerId: string) => void;
  onAssignExisting?: (managerId: string) => void;
  isAdmin?: boolean;
  drag: DragCtx;
}> = ({ node, depth, selectedId, onSelect, collapsed, onToggleCollapse, onSelectUser, onInviteUnder, onAssignExisting, isAdmin, drag }) => {
  const hasReports = node.reports.length > 0;
  const isCollapsed = collapsed.has(node.id);
  const isSelected = selectedId === node.id;
  const roleBorder = ROLE_BORDER[node.system_role] || ROLE_BORDER.user;
  const roleBg = ROLE_BG[node.system_role] || ROLE_BG.user;

  const isDragging = drag.draggedId === node.id;
  const isDragOver = drag.dropTargetId === node.id;
  const isValid = isDragOver && drag.isValidDrop(node.id);

  return (
    <div className={`relative transition-opacity ${isDragging ? 'opacity-40' : ''}`}>
      {/* Vertical line from parent */}
      {depth > 0 && (
        <div
          className="absolute bg-surface-secondary"
          style={{ left: depth * 36 - 18, top: 0, bottom: hasReports && !isCollapsed ? 0 : '50%', width: 1 }}
        />
      )}

      {/* Horizontal line to node */}
      {depth > 0 && (
        <div
          className="absolute top-1/2 -translate-y-1/2 h-px bg-surface-secondary"
          style={{ left: depth * 36 - 18, width: 18 }}
        />
      )}

      {/* Node Card */}
      <div
        data-node-card
        className={`
          relative flex items-center gap-3 p-3 pr-4 rounded-xl border transition-all cursor-pointer
          bg-gradient-to-r ${roleBg}
          ${isValid
            ? 'border-cyan-400/60 shadow-lg shadow-cyan-400/10 ring-1 ring-cyan-400/30'
            : isSelected
              ? 'border-brand/40 shadow-lg shadow-brand/10 ring-1 ring-brand/20'
              : `${roleBorder} hover:border-strong`
          }
        `}
        style={{ marginLeft: depth * 36 }}
        onClick={() => onSelect(node.id)}
        onDragOver={(e) => drag.onNodeDragOver(e, node.id)}
        onDrop={(e) => drag.onNodeDrop(e, node.id)}
      >
        {/* Drag Handle (admin only, when drag is enabled) */}
        {isAdmin && drag.canDrag && (
          <div
            draggable
            onDragStart={(e) => {
              const card = (e.target as HTMLElement).closest('[data-node-card]');
              if (card) e.dataTransfer.setDragImage(card as HTMLElement, 20, 20);
              drag.onDragStart(e, node.id);
            }}
            onDragEnd={drag.onDragEnd}
            className="flex items-center cursor-grab active:cursor-grabbing text-content-muted hover:text-content-tertiary transition-colors shrink-0 -ml-1"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical size={14} />
          </div>
        )}

        {/* Collapse Toggle */}
        {hasReports && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleCollapse(node.id); }}
            className="p-0.5 rounded text-content-faint hover:text-content-tertiary transition-colors cursor-pointer border-none outline-none shrink-0"
          >
            {isCollapsed
              ? <ChevronRight size={14} />
              : <ChevronDown size={14} />
            }
          </button>
        )}
        {!hasReports && <div className="w-[18px]" />}

        {/* Avatar */}
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand/25 to-status-success/25 flex items-center justify-center text-xs font-bold text-content-inverse shrink-0">
          {(node.display_name || node.email)[0]?.toUpperCase()}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-content truncate">
              {node.display_name || node.email.split('@')[0]}
            </span>
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${ROLE_DOT[node.system_role] || ROLE_DOT.user}`} />
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {node.title && (
              <span className="text-[11px] text-content-muted truncate">{node.title}</span>
            )}
          </div>
        </div>

        {/* Drop hint (replaces reports count during valid hover) */}
        {isValid && (
          <span className="text-[10px] text-cyan-400/80 font-medium shrink-0 animate-fade-in whitespace-nowrap">
            Drop to assign
          </span>
        )}

        {/* Reports count */}
        {hasReports && !isValid && (
          <span className="flex items-center gap-1 text-[10px] text-content-muted shrink-0 tabular-nums">
            <Users size={10} />
            {node.reports.length}
          </span>
        )}
      </div>

      {/* Selected Actions Bar — hidden while dragging to reduce noise */}
      {isSelected && isAdmin && !drag.draggedId && (
        <div
          className="flex items-center gap-1 mt-1 mb-1 animate-fade-in"
          style={{ marginLeft: depth * 36 + 8 }}
        >
          {onSelectUser && (
            <button
              onClick={() => onSelectUser(node.id)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-medium text-brand/80 bg-brand/10 hover:bg-brand/15 transition-colors cursor-pointer border border-brand/15 outline-none"
            >
              <ArrowUpRight size={10} />
              Edit Details
            </button>
          )}
          {onAssignExisting && (
            <button
              onClick={() => onAssignExisting(node.id)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-medium text-amber-400/80 bg-amber-400/10 hover:bg-amber-400/15 transition-colors cursor-pointer border border-amber-400/15 outline-none"
            >
              <Users size={10} />
              Assign Member
            </button>
          )}
          {onInviteUnder && (
            <button
              onClick={() => onInviteUnder(node.id)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-medium text-status-success/80 bg-status-success/10 hover:bg-status-success/15 transition-colors cursor-pointer border border-status-success/15 outline-none"
            >
              <UserPlus size={10} />
              Invite Member
            </button>
          )}
        </div>
      )}

      {/* Children */}
      {hasReports && !isCollapsed && (
        <div className="relative mt-1 space-y-1">
          {node.reports.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              collapsed={collapsed}
              onToggleCollapse={onToggleCollapse}
              onSelectUser={onSelectUser}
              onInviteUnder={onInviteUnder}
              onAssignExisting={onAssignExisting}
              isAdmin={isAdmin}
              drag={drag}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main Tree View ──

const OrgTreeView: React.FC<OrgTreeViewProps> = ({
  tree,
  loading,
  users = [],
  isAdmin = false,
  onSelectUser,
  onReassignManager,
  onInviteUnder,
  onAssignExisting,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // ── Drag state ──
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [unassignDragOver, setUnassignDragOver] = useState(false);
  const unassignCounter = useRef(0);

  const toggleCollapse = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Find unassigned users (not in tree)
  const treeUserIds = useMemo(() => {
    const ids = new Set<string>();
    const walk = (nodes: OrgTreeNode[]) => {
      for (const n of nodes) {
        ids.add(n.id);
        walk(n.reports);
      }
    };
    walk(tree);
    return ids;
  }, [tree]);

  const unassignedUsers = useMemo(
    () => users.filter((u) => !treeUserIds.has(u.id) && u.status !== 'deactivated'),
    [users, treeUserIds]
  );

  // ── Drag-and-drop logic ──

  const isValidDrop = useCallback((targetId: string): boolean => {
    if (!draggedId) return false;
    if (targetId === draggedId) return false;
    // Prevent cycles: can't drop onto own descendant
    const draggedNode = findNode(tree, draggedId);
    if (draggedNode && isInSubtree(draggedNode, targetId)) return false;
    return true;
  }, [draggedId, tree]);

  const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
    // Timeout so browser captures ghost image before we dim the source
    setTimeout(() => setDraggedId(id), 0);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDropTargetId(null);
    setUnassignDragOver(false);
    unassignCounter.current = 0;
  }, []);

  const handleNodeDragOver = useCallback((e: React.DragEvent, nodeId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetId(nodeId);
  }, []);

  const handleNodeDrop = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const userId = e.dataTransfer.getData('text/plain');
    if (userId && userId !== targetId && onReassignManager) {
      const draggedNode = findNode(tree, userId);
      const wouldCycle = draggedNode ? isInSubtree(draggedNode, targetId) : false;
      if (!wouldCycle) {
        onReassignManager(userId, targetId);
      }
    }
    setDraggedId(null);
    setDropTargetId(null);
  }, [onReassignManager, tree]);

  // Unassign drop zone handlers
  const handleUnassignDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleUnassignDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    unassignCounter.current++;
    setUnassignDragOver(true);
  }, []);

  const handleUnassignDragLeave = useCallback(() => {
    unassignCounter.current--;
    if (unassignCounter.current <= 0) {
      setUnassignDragOver(false);
      unassignCounter.current = 0;
    }
  }, []);

  const handleUnassignDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const userId = e.dataTransfer.getData('text/plain');
    if (userId && onReassignManager && treeUserIds.has(userId)) {
      onReassignManager(userId, null);
    }
    setDraggedId(null);
    setDropTargetId(null);
    setUnassignDragOver(false);
    unassignCounter.current = 0;
  }, [onReassignManager, treeUserIds]);

  // Memoized drag context passed to all tree nodes
  const dragCtx: DragCtx = useMemo(() => ({
    canDrag: !!onReassignManager,
    draggedId,
    dropTargetId,
    isValidDrop,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onNodeDragOver: handleNodeDragOver,
    onNodeDrop: handleNodeDrop,
  }), [draggedId, dropTargetId, isValidDrop, handleDragStart, handleDragEnd, handleNodeDragOver, handleNodeDrop, onReassignManager]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-status-success/30 border-t-success rounded-full animate-spin" />
      </div>
    );
  }

  if (tree.length === 0 && unassignedUsers.length === 0) {
    return (
      <div className="text-center py-16">
        <GitBranch size={32} className="text-content-faint mx-auto mb-3" />
        <p className="text-sm text-content-muted mb-1">No reporting structure set up yet</p>
        <p className="text-xs text-content-faint">
          Open any team member's profile in the People tab and assign a manager using the "Reports To" field.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap">
        {Object.entries(ROLE_LABELS).map(([role, label]) => (
          <div key={role} className="flex items-center gap-1.5 text-[10px] text-content-muted">
            <span className={`w-2 h-2 rounded-full ${ROLE_DOT[role]}`} />
            {label}
          </div>
        ))}
        {isAdmin && onReassignManager && (
          <div className="flex items-center gap-1.5 text-[10px] text-content-faint ml-auto select-none">
            <GripVertical size={10} className="text-content-faint" />
            Drag to reassign
          </div>
        )}
      </div>

      {/* Tree */}
      <div className="space-y-1">
        {tree.map((root) => (
          <TreeNode
            key={root.id}
            node={root}
            depth={0}
            selectedId={selectedId}
            onSelect={setSelectedId}
            collapsed={collapsed}
            onToggleCollapse={toggleCollapse}
            onSelectUser={onSelectUser}
            onInviteUnder={onInviteUnder}
            onAssignExisting={onAssignExisting}
            isAdmin={isAdmin}
            drag={dragCtx}
          />
        ))}
      </div>

      {/* Unassign Drop Zone — only visible while dragging a tree node */}
      {draggedId && treeUserIds.has(draggedId) && (
        <div
          className={`
            flex items-center justify-center gap-2 py-4 rounded-xl border-2 border-dashed transition-all animate-fade-in
            ${unassignDragOver
              ? 'border-amber-400/50 bg-amber-400/[0.08] text-amber-400/80'
              : 'border-subtle text-content-faint'
            }
          `}
          onDragOver={handleUnassignDragOver}
          onDragEnter={handleUnassignDragEnter}
          onDragLeave={handleUnassignDragLeave}
          onDrop={handleUnassignDrop}
        >
          <span className="text-xs font-medium">Drop here to remove manager</span>
        </div>
      )}

      {/* Unassigned Users Pool */}
      {unassignedUsers.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} className="text-amber-400/70" />
            <span className="text-xs font-medium text-amber-400/70">
              {unassignedUsers.length} unassigned {unassignedUsers.length === 1 ? 'person' : 'people'}
            </span>
            <span className="text-[10px] text-content-faint">— no manager assigned, not visible in the hierarchy</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {unassignedUsers.map((user) => (
              <div
                key={user.id}
                data-user-card
                className={`
                  relative flex items-center gap-3 p-3 rounded-xl bg-amber-400/[0.03] border border-amber-400/10
                  hover:border-amber-400/20 transition-all
                  ${draggedId === user.id ? 'opacity-40' : ''}
                `}
              >
                {/* Drag Handle for unassigned users (admin only) */}
                {isAdmin && onReassignManager && (
                  <div
                    draggable
                    onDragStart={(e) => {
                      const card = (e.target as HTMLElement).closest('[data-user-card]');
                      if (card) e.dataTransfer.setDragImage(card as HTMLElement, 20, 20);
                      handleDragStart(e, user.id);
                    }}
                    onDragEnd={handleDragEnd}
                    className="flex items-center cursor-grab active:cursor-grabbing text-amber-400/40 hover:text-amber-400/60 transition-colors shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <GripVertical size={14} />
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => onSelectUser?.(user.id)}
                  className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer bg-transparent border-none outline-none text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-amber-400/10 flex items-center justify-center text-[11px] font-bold text-amber-400 shrink-0">
                    {(user.display_name || user.email)[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs font-medium text-content truncate block">
                      {user.display_name || user.email.split('@')[0]}
                    </span>
                    <span className="text-[10px] text-content-muted truncate block">
                      {user.title || user.email}
                    </span>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(OrgTreeView);
